<!--
aktuellen Status der Sensoren in Tabelle anzeigen
-->
<template>

  <div class="sensorStatusWrapper">

    <transition name="fade" mode="out-in">

      <div v-if="sensoren">

        <!-- Tabelle Filtern -->
        <div id="filter" class="row border-top mb-3 pt-3">

          <div class="col-md-11">

            <b-input-group size="sm">
              <b-form-input v-model="keyword"
                            type="search"
                            id="filterInput"
                            :placeholder="$t('message.positionen_filter')"
              />
              <b-input-group-append>
                <b-button :disabled="!keyword" @click="keyword = ''"><span class="oi oi-x"></span></b-button>
              </b-input-group-append>
            </b-input-group>

          </div>
          <div class="col-md-1 text-right">

            <b-form-group horizontal label="" class="">
              <b-form-select :options="pageOptions" v-model="perPage" size="sm" />
            </b-form-group>

          </div>

        </div>

        <div v-if="pendingResultRequests > 0"
             class="alert alert-info py-2 mb-2 d-flex align-items-center"
             style="font-weight: bold;">
          <b-icon icon="arrow-clockwise" animation="spin" font-scale="1.2" class="mr-2"></b-icon>
          {{ $t('message.sensorStatus_lade_ergebnisse', { count: pendingResultRequests }) }}
        </div>

        <div class="table-responsive">
          <b-table striped
                 id="sensor-status-table"
                 small
                 :fields="fields"
                 :items="items"
                 :keyword="keyword"
                 :current-page="currentPage"
                 :per-page="perPage"
                 hover
                 :responsive="true">

          <template v-slot:cell(lastSensorResult_alarmLevel)="row">

            <span v-if="row.item.sensor && loadingSensorIds[row.item.sensor.id]"
                  style="white-space: nowrap;">
              <b-icon icon="arrow-clockwise" animation="spin" font-scale="1"></b-icon>
              <span class="text-muted ml-1">{{ $t('message.sensorStatus_laedt') }}</span>
            </span>
            <span v-else-if="row.item.lastSensorResult && row.item.lastSensorResult.temperatur != null">
                <sensor-ergebnis v-model="row.item" :sensor="row.item.sensor" />
            </span>
            <span v-else style="color: #999;">&mdash;</span>

          </template>
          <template v-slot:cell(lastSensorResult_timestamp)="row">

            <span v-if="row.item.sensor && loadingSensorIds[row.item.sensor.id]"
                  style="white-space: nowrap;">
              <b-icon icon="arrow-clockwise" animation="spin" font-scale="1"></b-icon>
              <span class="text-muted ml-1">{{ $t('message.sensorStatus_laedt') }}</span>
            </span>
            <span v-else-if="row.item.lastSensorResult && row.item.lastSensorResult.timestamp">
              <java-date-to-default-time-format v-model="row.item.lastSensorResult.timestamp" />
            </span>

          </template>
          <template v-slot:cell(batteryStatus)="row">

            <span v-if="row.item.lastSensorResult">
              <batterie-status v-model="row.item.lastSensorResult.batteryStatus" :sensor="row.item.sensor" />
            </span>

          </template>
          <template v-slot:cell(sensor_name)="row">

            <span v-if="row.item.sensor.name">
              {{ row.item.sensor.name }}
            </span>
            <span v-else-if="row.item.sensor.code">
               {{ row.item.sensor.code }}
            </span>

          </template>
          <template v-slot:cell(position)="row">

            <span v-if="row.item.position">
               <PositionOfTraplinkedDevice :pos="row.item.position" />
            </span>

          </template>
          <template v-slot:cell(objekt)="row">

            <span v-if="row.item.objekt">
              <objekt-of-traplinked-device :object="row.item.objekt" />
            </span>

          </template>
          <template v-slot:cell(area)="row">

            <span v-if="row.item.area">
              <area-of-traplinked-device :bereich="row.item.area" />
            </span>

          </template>
          <template v-slot:cell(rotAlarmNachMinuten)="row">

            <b-input-group v-if="row.item.sensor && row.item.sensor.art === 'TUER'"
                           size="sm"
                           :append="$t('message.sensorStatus_min_kurz')"
                           style="max-width: 110px;">
              <b-form-input
                  v-model="row.item.sensor.rotAlarmNachMinuten"
                  type="number"
                  min="1"
                  step="1"
                  :placeholder="String(defaultRotAlarmNachMinuten)"
                  @blur="saveRotAlarmNachMinuten(row.item.sensor)"
              />
            </b-input-group>
            <span v-else style="color: #999;">&mdash;</span>

          </template>
          <template v-slot:cell(actions)="row">

            <span v-if="row.item.lastSensorResult && row.item.lastSensorResult.temperatur != null">
              <sensor-history v-model="row.item.sensor" />
            </span>

          </template>

        </b-table>
        </div>

        <b-row>
          <b-col md="12" class="my-1">
            <b-pagination :total-rows="totalRows"
                          :per-page="perPage"
                          v-model="currentPage"
                          class="my-0"
            />
          </b-col>
        </b-row>

      </div>

    </transition>

  </div>

</template>
<script>

  // Define a new component
  import axios from 'axios';
  import PositionOfTraplinkedDevice from '@/components/traplinkedbericht/PositionOfTraplinkedDevice';
  import BatterieStatus from '@/components/traplinkedbericht/BatterieStatus';
  import AreaOfTraplinkedDevice from '@/components/traplinkedbericht/AreaOfTraplinkedDevice';
  import JavaDateToDefaultTimeFormat from '@/components/layout/JavaDateToDefaultTimeFormat';
  import ObjektOfTraplinkedDevice from '@/components/traplinkedbericht/ObjektOfTraplinkedDevice.vue';
  import SensorErgebnis from '@/components/sensor/SensorErgebnis.vue';
  import SensorHistory from '@/components/sensor/SensorHistory.vue';

  export default {

    name: 'SensorStatus',
    components: {
      SensorHistory,
      ObjektOfTraplinkedDevice,
      BatterieStatus,
      PositionOfTraplinkedDevice,
      AreaOfTraplinkedDevice,
      JavaDateToDefaultTimeFormat,
      SensorErgebnis
    },
    props: ['value'], // Liste mit Sensoren
    /*
      Defines the data used by the component
    */
    data: function () {
      return {
        /** Liste von Sensoren eines Kunden */
        sensoren: null,
        fields: [
          {
            key: 'lastSensorResult_timestamp',
            label: this.$t('message.traplinkedStatus_letzte_meldung'),
            sortable: true,
          },
          {
            key: 'sensor_name',
            label: this.$t('message.sensorStatus_name'),
            sortable: true,
          },
          {
            key: 'objekt',
            label: this.$t('message.printErgebnisliste_objekt'),
            sortable: true,
          },
          {
            key: 'position',
            label: this.$t('message.printPositionen_position'),
            sortable: true,
          },
          {
            key: 'area',
            label: this.$t('message.printPositionen_bereich'),
            sortable: false,
          },
          {
            key: 'batteryStatus',
            label: this.$t('message.traplinkedStatus_batterie'),
            sortable: true,
          },
          {
            key: 'lastSensorResult_alarmLevel',
            label: this.$t('message.printErgebnisliste_ergebnis'),
            sortable: true,
          },
          {
            key: 'rotAlarmNachMinuten',
            label: this.$t('message.sensorStatus_roter_alarm_nach_minuten'),
            sortable: false,
          },
          {
            key: 'actions',
            label: this.$t('message.traplinkedStatus_alt'),
            sortable: false,
            class: 'text-right'
          }
        ],
        /** Filter Query String für Suche nach Position */
        keyword: '',
        /** Anzahl der Positionen die in der Tabelle angezeigt werden sollen */
        pageOptions: [ 5, 10, 15, 20, 50, 100 ],
        /** ausgewählter Wert aus den pageOptions */
        perPage: 20,
        /** Pagination der Ergebnistabelle */
        currentPage: 1,
        /** Map sensor.id -> true|false, true wenn getLastSensorResult für den Sensor gerade läuft */
        loadingSensorIds: {},
        /** Anzahl der ausstehenden getLastSensorResult-Calls (für globalen Loading-Hinweis) */
        pendingResultRequests: 0,
        /** Standardwert (Minuten) wenn am Türsensor kein eigener Wert erfasst ist, siehe DataBroker sensor.default_rot_alarm_nach_minuten */
        defaultRotAlarmNachMinuten: 5,
      }
    },
    computed: {

      items: function () {

        const lowercase = this.keyword ? this.keyword.toLowerCase() : null

        const selectedRows = lowercase
            ? this.traplinkedDevices.filter( item =>
                (item.lastSensorResult && item.lastSensorResult.timestamp && this.javaDateToDefaultTimeFormat(item.lastSensorResult.timestamp).toLowerCase().includes(lowercase)) ||
                (item.sensor.name && item.sensor.name.toLowerCase().includes(lowercase)) ||
                (item.sensor.code && item.sensor.code.toLowerCase().includes(lowercase)) ||
                (item.position && item.position.positionsNr && item.position.positionsNr.toLowerCase().includes(lowercase)) ||
                (item.position && item.position.positionsBesch && item.position.positionsBesch.toLowerCase().includes(lowercase)) ||
                (item.objekt && item.objekt.bezeichnung && item.objekt.bezeichnung.toLowerCase().includes(lowercase)) ||
                (item.area && item.area.name && item.area.name.toLowerCase().includes(lowercase))
            )
            : this.sensoren

        return selectedRows

      },
      totalRows: function () {
        return this.items.length
      }

    },
    watch: {

      value: function () {
        this.init()
      }

    },
    mounted: function () {

      this.init()

    },
    methods: {

      /** eine Area in die Liste der Sensoren zum Filtern eintragen */
      setArea: function (obj) {

        console.log('setArea' + obj)
        for (let i = 0; i < this.sensoren.length; i++) {
          if (this.sensoren[i].id == obj.traplinked_device_id) {
            this.sensoren[i].area = obj.area
            break
          }
        }

      },
      /** eine Position in die Liste der Sensoren zum Filtern eintragen */
      setPosition: function (obj) {

        console.log('setPosition' + obj);
        for (let i = 0; i < this.sensoren.length; i++) {
          if (this.sensoren[i].id == obj.traplinked_device_id) {
            this.sensoren[i].position = obj.position
            break
          }
        }

      },
      /** ein Objekt in die Liste der Sensoren zum Filtern eintragen */
      setObjekt: function (obj) {

        console.log('setObjekt', obj);
        for (let i = 0; i < this.sensoren.length; i++) {
          if (this.sensoren[i].id == obj.traplinked_device_id) {
            this.sensoren[i].objekt = obj.objekt
            break
          }
        }

      },
      /** hole alle Sensoren des Kunden */
      init: function () {

        this.sensoren = null

        if (!this.value) {

          this.showSpinner()
          const customer = this.$store.getters.customer
          const url      = process.env.VUE_APP_SERVER_URL + '/datenbroker/getSensoren'
          const params   = {
            customer_id: customer.id
          }

          const that = this;
          axios({
            method: 'GET',
            params: params,
            url: url
          }).then(response => {

            if (response.data && response.data.sensorCustomer instanceof Array) {

              that.sensoren = response.data.sensorCustomer
              that.hideSpinner()
              that.loadLastSensorResults()

            }

          }).catch(error => {
            console.log(error)
          })

        } else {

          this.sensoren = this.value
          this.loadLastSensorResults()

        }

      },
      /**
       * lädt für jeden Sensor das letzte Sensor Ergebnis separat vom Server,
       * da das mitliefern in getSensoren serverseitig zu lange dauert
       */
      loadLastSensorResults: function () {

        if (!(this.sensoren instanceof Array)) {
          return
        }

        // wenn noch Calls aus einem vorherigen Aufruf laufen, nicht erneut starten -
        // sonst feuern wir bei jedem value-Watcher-Trigger 6 weitere Calls los und
        // der Counter explodiert (6 -> 12 -> 18 -> ...).
        if (this.pendingResultRequests > 0) {
          console.log('loadLastSensorResults: skip, ' + this.pendingResultRequests + ' Calls noch in flight')
          return
        }

        const customer = this.$store.getters.customer
        const url      = process.env.VUE_APP_SERVER_URL + '/datenbroker/getLastSensorResult'

        // Loading-Map mit allen Sensor-IDs auf true vorinitialisieren, danach
        // einmal als neue Reference setzen — so sind alle Keys von Anfang an
        // reaktiv, nachträgliche Updates per $set lösen Re-Renders aus
        const loadingMap = {}
        this.sensoren.forEach(item => {
          if (item && item.sensor && item.sensor.id) {
            loadingMap[item.sensor.id] = true
          }
        })
        this.loadingSensorIds = loadingMap
        console.log('loadLastSensorResults: loadingSensorIds initial =', JSON.parse(JSON.stringify(this.loadingSensorIds)))

        this.sensoren.forEach(item => {

          if (!item || !item.sensor || !item.sensor.id) {
            return
          }

          this.pendingResultRequests++

          axios({
            method: 'GET',
            params: {
              sensor_id:   item.sensor.id,
              customer_id: customer ? customer.id : null
            },
            url: url
          }).then(response => {

            const result = response.data ? response.data.sensorResult : null
            this.$set(item, 'lastSensorResult', result || null)

          }).catch(error => {
            console.log(error)
          }).finally(() => {
            console.log('loadLastSensorResults: clearing loading for sensor', item.sensor.id)
            this.$set(this.loadingSensorIds, item.sensor.id, false)
            this.pendingResultRequests = Math.max(0, this.pendingResultRequests - 1)
          })

        })

      },
      /**
       * speichert die für einen Türsensor konfigurierte Zeit (in Minuten), nach der ein
       * andauernder GELB-Alarm automatisch zu ROT eskaliert wird
       */
      saveRotAlarmNachMinuten: function (sensor) {

        if (!sensor || !sensor.id) {
          return
        }

        const that = this
        const url  = process.env.VUE_APP_SERVER_URL + '/datenbroker/saveRotAlarmNachMinuten'
        axios({
          method: 'GET',
          params: {
            sensor_id:           sensor.id,
            rotAlarmNachMinuten: sensor.rotAlarmNachMinuten || null
          },
          url: url
        }).then(() => {

          console.log('rotAlarmNachMinuten gespeichert.')
          that.$bvToast.toast(that.$t('message.traplinkedImages_success'), {
            title: that.$t('message.traplinkedImages_success_title'),
            variant: 'success',
            autoHideDelay: 3000
          })

        }).catch(error => {
          console.log(error)
          that.$bvToast.toast(that.$t('message.sensorStatus_rotAlarm_error'), {
            title: that.$t('message.sensorStatus_rotAlarm_error_title'),
            variant: 'danger',
            autoHideDelay: 5000
          })
        })

      }

    }

  }

</script>
